import React, { useState, useEffect } from "react";
import axios from "axios";
import CanvasJSReact from "@canvasjs/react-charts";
import { VscOpenPreview } from "react-icons/vsc";
import { FaEdit, FaExpandAlt } from "react-icons/fa";
import DrawingOverlay from "./DrawingOverlay";
import { FaUser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

function NewEnrolment({
  expanded,
  setExpanded,
  selectedYear,
  selectedQuarter,
}) {
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [currentData, setCurrentData] = useState([]);
  const [previousData, setPreviousData] = useState([]);
  const [currentLevelData, setCurrentLevelData] = useState([]);
  const [progressionCurrent, setProgressionCurrent] = useState([]);
  // const [progressionPrevious, setProgressionPrevious] = useState([]);
  const [progressionTotals, setProgressionTotals] = useState({
    eligible: 0,
    withdrawn: 0,
    fail: 0,
    reenrolled: 0,
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  function groupProgressionByCurrentCourse(data) {
    const grouped = {};

    data.forEach((entry) => {
      const course = entry.current_course;

      if (!grouped[course]) {
        grouped[course] = {
          current_course: course,
          eligible_count: 0,
          withdrawal_count: 0,
          rejected_count: 0,
          dismissed_count: 0,
          failed_count: 0,
          transfer_course_count: 0,
          current_status_count: 0,
          deferred_count: 0,
          enrolled_count: 0,
        };
      }

      grouped[course].eligible_count += Number(entry.eligible_count);
      grouped[course].withdrawal_count += Number(entry.withdrawal_count);
      grouped[course].rejected_count += Number(entry.rejected_count);
      grouped[course].dismissed_count += Number(entry.dismissed_count);
      grouped[course].failed_count += Number(entry.failed_count);
      grouped[course].transfer_course_count += Number(
        entry.transfer_course_count
      );
      grouped[course].current_status_count += Number(
        entry.current_status_count
      );
      grouped[course].deferred_count += Number(entry.deferred_count);
      grouped[course].enrolled_count += Number(entry.enrolled_count);
    });

    return Object.values(grouped);
  }

  const convertDataForChart = () => {
    const currentMap = new Map();
    const previousMap = new Map();

    currentData.forEach((item) => {
      currentMap.set(item.course_code, item.totalStudent);
    });
    previousData.forEach((item) => {
      previousMap.set(item.course_code, item.totalStudent);
    });

    const labels = Array.from(
      new Set([...currentMap.keys(), ...previousMap.keys()])
    );

    const currentPoints = labels.map((label) => ({
      label,
      y: currentMap.get(label) || 0,
    }));

    const previousPoints = labels.map((label) => ({
      label,
      y: previousMap.get(label) || 0,
    }));

    return { currentPoints, previousPoints };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getNewEnrollmentByCourse2?qtr=${selectedQuarter}&year=${selectedYear}`
        );

        if (res.data.success) {
          setCurrentData(res.data.current || []);
          setPreviousData(res.data.previous || []);
        } else {
          console.error("Failed to fetch data:", res.data.message);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchDataLevel = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getNewEnrollmentByLevel2`,
          {
            params: { qtr: selectedQuarter, year: selectedYear },
          }
        );
        if (res.data.success) {
          setCurrentLevelData(res.data.current || []);
        }
      } catch (error) {
        console.error("Error fetching new enrollment level data:", error);
      }
    };

    const fetchProgressionData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getProgressionRate`,
          {
            params: {
              qtr: selectedQuarter,
              year: selectedYear,
            },
          }
        );
        if (res.data.success) {
          const currentData = res.data.current.summary || [];
          setProgressionCurrent(currentData);

          let eligible = 0;
          let withdrawn = 0;
          let fail = 0;
          let reenrolled = 0;

          currentData.forEach((item) => {
            eligible += Number(item.eligible_count) || 0;
            withdrawn += Number(item.withdrawal_count) || 0;
            fail += Number(item.failed_count) || 0;
            reenrolled += Number(item.enrolled_count) || 0;
          });

          setProgressionTotals({
            eligible,
            withdrawn,
            fail,
            reenrolled,
          });
        } else {
          console.error("Failed to fetch progression data:", res.data.message);
        }
      } catch (error) {
        console.error("Error fetching progression data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProgressionData();
    fetchData();
    fetchDataLevel();
  }, [selectedQuarter, selectedYear]);

  const handleViewTable = () => {
    const currentQuarter = `Q${selectedQuarter} ${selectedYear}`;
    const previousQuarter =
      selectedQuarter === 1
        ? `Q4 ${selectedYear - 1}`
        : `Q${selectedQuarter - 1} ${selectedYear}`;

    navigate("/detailnewenrol", {
      state: {
        selectedQuarters: [currentQuarter, previousQuarter],
        currentData: currentData.current,
        previousData: currentData.previous,
      },
    });
  };

  const handleViewProgression = () => {
    const currentQuarter = `Q${selectedQuarter} ${selectedYear}`;
    const previousQuarter =
      selectedQuarter === 1
        ? `Q4 ${selectedYear - 1}`
        : `Q${selectedQuarter - 1} ${selectedYear}`;

    navigate("/detailreenrol", {
      state: {
        selectedQuarters: [currentQuarter, previousQuarter],
        currentData: currentData.current,
        previousData: currentData.previous,
      },
    });
  };

  const prepareProgressionStackData = () => {
    const groupedData = groupProgressionByCurrentCourse(progressionCurrent);

    const eligiblePoints = [];
    const withdrawnPoints = [];
    const enrolledPoints = [];
    const failPoints = [];

    groupedData.forEach((entry) => {
      eligiblePoints.push({
        label: entry.current_course,
        y: entry.eligible_count,
      });
      withdrawnPoints.push({
        label: entry.current_course,
        y: entry.withdrawal_count,
      });
      enrolledPoints.push({
        label: entry.current_course,
        y: entry.enrolled_count,
      });
      failPoints.push({
        label: entry.current_course,
        y: entry.failed_count,
      });
    });

    return { eligiblePoints, withdrawnPoints, enrolledPoints, failPoints };
  };

  const prepareProgressionPieData = () => {
    const { eligible, withdrawn, fail, reenrolled } = progressionTotals;
    const colors = ["#4169E1", "#FFA500", "#e74c3c", "#3CB371"];

    return [
      { y: eligible, label: "Eligible", color: colors[0] },
      { y: withdrawn, label: "Withdrawn", color: colors[1] },
      { y: fail, label: "Failed", color: colors[2] },
      { y: reenrolled, label: "Re-Enrolled", color: colors[3] },
    ];
  };

  const preparePieData = () => {
    const colors = [
      "#FFA726",
      "#66BB6A",
      "#42A5F5",
      "#AB47BC",
      "#FF7043",
      "#26C6DA",
      "#5D5FEF",
      "#8884d8",
    ];
    return currentLevelData.map((item, index) => ({
      label: item.course_level?.trim() || "Uncategorized",
      y: Number(item.totalLevel) || 0,
      color: colors[index % colors.length],
    }));
  };

  const totalEnrollment = currentLevelData.reduce(
    (sum, item) => sum + (Number(item.totalLevel) || 0),
    0
  );

  const headerStyle = {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 10,
  };

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const chartBoxStyle = {
    backgroundColor: "#2f2f2f",
    borderRadius: 10,
    padding: 20,
    minWidth: 300,
    height: "620px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    boxShadow:
      "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
  };

  const iconStyle = {
    position: "absolute",
    top: 15,
    right: 15,
    color: "#bbb",
    cursor: "pointer",
  };

  const highLowBox = {
    backgroundColor: "#2C2C2C",
    borderRadius: 10,
    padding: 20,
    // flex: 1,
    boxShadow:
      "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
  };

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#373636",
        margin: 20,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h5 style={{ color: "#fff", margin: 0 }}>
          Re-Enrollment and New Enrollment
        </h5>

        <div style={{ display: "flex", gap: 12 }}>
          <FaEdit
            size={20}
            style={{ color: "#fff", cursor: "pointer" }}
            onClick={() => setIsDrawingMode(!isDrawingMode)}
          />
          <FaExpandAlt
            size={20}
            onClick={handleToggleExpand}
            style={{ color: "#fff", cursor: "pointer" }}
            title={expanded ? "Collapse" : "Expand"}
          />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 1,
            minWidth: 250,
            height: "400px",
          }}
        >
          <div
            style={{
              ...highLowBox,
              minHeight: "300px",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
            }}
          >
            <div style={{ color: "#aaa", fontSize: 14, marginBottom: 10 }}>
              Re-Enrollment
            </div>
            <CanvasJSChart
              options={{
                backgroundColor: "#2C2C2C",
                animationEnabled: true,
                legend: {
                  fontColor: "#fff",
                  fontSize: 14,
                  verticalAlign: "bottom",
                  horizontalAlign: "center",
                },
                data: [
                  {
                    type: "pie",
                    showInLegend: true,
                    legendText: "{label}",
                    indexLabel: "{label} ({y})",
                    indexLabelFontColor: "#fff",
                    indexLabelFontSize: 14,
                    dataPoints: prepareProgressionPieData(),
                  },
                ],
              }}
            />
          </div>

          <div
            style={{
              ...highLowBox,
              minHeight: "300px",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <span style={{ color: "#aaa", fontSize: 14 }}>
                New Enrollment
              </span>

              <span
                style={{
                  backgroundColor: "#555",
                  color: "#fff",
                  fontWeight: "bold",
                  borderRadius: 20,
                  padding: "4px 12px",
                  fontSize: 13,
                  display: "inline-block",
                  minWidth: 28,
                  textAlign: "center",
                }}
              >
                <FaUser /> {totalEnrollment}
              </span>
            </div>

            <CanvasJSChart
              options={{
                backgroundColor: "#2C2C2C",
                animationEnabled: true,
                legend: {
                  fontColor: "#fff",
                  fontSize: 14,
                  verticalAlign: "bottom",
                  horizontalAlign: "center",
                },
                data: [
                  {
                    type: "pie",
                    showInLegend: true,
                    legendText: "{label}",
                    indexLabel: "{label} ({y})",
                    indexLabelFontColor: "#fff",
                    indexLabelFontSize: 14,
                    dataPoints: preparePieData(),
                  },
                ],
              }}
            />
          </div>
        </div>

        {/* Column 2: Bar Charts */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 2,
            minWidth: 350,
          }}
        >
          <div style={chartBoxStyle}>
            <div style={{ ...iconStyle, display: "flex", gap: 10 }}>
              <div title="View Re-Enrollment Table">
                <VscOpenPreview size={18} onClick={handleViewProgression} />
              </div>
            </div>
            <div style={headerStyle} className="mb-4">
              RE-ENROLLMENT (Q{selectedQuarter} - {selectedYear})
            </div>
            {!loading &&
              (() => {
                const {
                  eligiblePoints,
                  withdrawnPoints,
                  enrolledPoints,
                  failPoints,
                } = prepareProgressionStackData();

                const stackoptions = {
                  height: 500,
                  animationEnabled: true,
                  backgroundColor: "#2f2f2f",
                  axisX: {
                    title: "Current Course",
                    labelFontColor: "#ccc",
                    titleFontColor: "#ccc",
                    interval: 1,
                    labelFontSize: 12,
                  },
                  axisY: {
                    title: "Student Count",
                    labelFontColor: "#ccc",
                    titleFontColor: "#ccc",
                    gridColor: "#444",
                  },
                  toolTip: { shared: true },
                  legend: {
                    cursor: "pointer",
                    fontColor: "#fff",
                    fontSize: 12,
                  },
                  data: [
                    {
                      type: "stackedColumn",
                      name: "Eligible for Re-enrollment",
                      showInLegend: true,
                      color: "#4169E1",
                      dataPoints: eligiblePoints,
                    },
                    {
                      type: "stackedColumn",
                      name: "Withdrawn",
                      showInLegend: true,
                      color: "#FFA500",
                      dataPoints: withdrawnPoints,
                    },
                    {
                      type: "stackedColumn",
                      name: "Total Enrolled",
                      showInLegend: true,
                      color: "#3CB371",
                      dataPoints: enrolledPoints,
                    },
                    {
                      type: "stackedColumn",
                      name: "Total Failed",
                      showInLegend: true,
                      color: "#e74c3c",
                      dataPoints: failPoints,
                    },
                  ],
                };

                return <CanvasJSChart options={stackoptions} />;
              })()}
          </div>
        </div>

        {/* Column 3: Bar Charts */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 2,
            minWidth: 350,
          }}
        >
          <div style={chartBoxStyle}>
            <div style={{ ...iconStyle, display: "flex", gap: 10 }}>
              <div title="View New Enrollment Table">
                <VscOpenPreview size={18} onClick={handleViewTable} />
              </div>
            </div>
            <div style={headerStyle} className="mb-4">
              NEW ENROLLMENT
            </div>
            {!loading &&
              (() => {
                const { currentPoints, previousPoints } = convertDataForChart();

                return (
                  <CanvasJSChart
                    options={{
                      height: 500,
                      animationEnabled: true,
                      backgroundColor: "#2f2f2f",
                      axisX: {
                        title: "Course",
                        labelFontColor: "#ccc",
                        titleFontColor: "#ccc",
                        reversed: true,
                        interval: 1,
                        labelFontSize: 12,
                        titleFontSize: 14,
                      },
                      axisY: {
                        title: "Student Count",
                        labelFontColor: "#ccc",
                        titleFontColor: "#ccc",
                        gridColor: "#444",
                      },
                      toolTip: { shared: true },
                      legend: {
                        cursor: "pointer",
                        fontColor: "#fff",
                        fontSize: 14,
                      },
                      data: [
                        {
                          type: "bar",
                          name: "Current",
                          showInLegend: true,
                          color: "#5D5FEF",
                          dataPoints: currentPoints,
                        },
                        {
                          type: "bar",
                          name: "Previous",
                          showInLegend: true,
                          color: "#8884d8",
                          dataPoints: previousPoints,
                        },
                      ],
                    }}
                  />
                );
              })()}
          </div>
        </div>
      </div>
      {isDrawingMode && (
        <DrawingOverlay onClose={() => setIsDrawingMode(false)} />
      )}
    </div>
  );
}

export default NewEnrolment;
